/* eslint-disable react/no-unstable-nested-components -- The `accessor` and `fields` props
   contain small render functions that produce JSX inline for each row; extracting them
   would fragment the tab's declarative config with no runtime benefit. */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api-error";
import {
  Building2,
  CreditCard,
  DatabaseBackup,
  FileText,
  FolderTree,
  Landmark,
  Lock,
  Pencil,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Tag as TagIcon,
  UserRound,
  Users2,
  X,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  createCategory,
  createVendor,
  deleteCategory,
  deleteVendor,
  listCategories,
  listVendors,
  reactivateCategory,
  reactivateVendor,
  updateCategory,
  updateVendor,
  // Nuove anagrafiche personalizzabili
  listPaymentMethods,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
  reactivatePaymentMethod,
  listBankAccounts,
  createBankAccount,
  updateBankAccount,
  deleteBankAccount,
  reactivateBankAccount,
  listTags,
  createTag,
  updateTag,
  deleteTag,
  reactivateTag,
  listDocumentTypes,
  createDocumentType,
  updateDocumentType,
  deleteDocumentType,
  reactivateDocumentType,
} from "@/lib/api";
import { BackupTab } from "@/components/BackupTab";
import { ARClientsTab, ARCategoriesTab } from "@/components/ARSettingsTabs";
import { StudioBrandingTab } from "@/components/StudioBrandingTab";
import { SecurityTab } from "@/components/SecurityTab";
import { SimpleCrudTab } from "@/components/SimpleCrudTab";
import { PreferencesTab } from "@/components/PreferencesTab";
// v1.60.5: `useAuth` non più necessario in Settings — il logout ora vive
// nella TopBar globale (Layout.jsx). Import rimosso per pulizia.

const EMPTY_FORM = {
  id: null,
  mode: "create", // 'create' | 'edit'
  name: "",
  tax_code: "",
  email: "",
  color: "#1F2937",
};

/**
 * Struttura navigazione delle Impostazioni.
 * Ogni voce definisce: key (route interna), label, subtitle, icona,
 * gruppo di appartenenza. Le sezioni sono raggruppate per famiglia
 * funzionale (Anagrafiche / Personalizzazioni / Preferenze / Sistema /
 * Sicurezza) e visualizzate come menu laterale.
 */
const SETTINGS_GROUPS = [
  {
    key: "anagrafiche",
    label: "Anagrafiche",
    items: [
      {
        key: "categories",
        label: "Categorie",
        subtitle: "Categorie di spesa per organizzare le movimentazioni",
        icon: FolderTree,
      },
      {
        key: "vendors",
        label: "Fornitori",
        subtitle: "Anagrafica fornitori collegati a spese e report",
        icon: Users2,
      },
      {
        key: "ar-clients",
        label: "Clienti",
        subtitle: "Anagrafica clienti collegati agli incassi",
        icon: UserRound,
      },
      {
        key: "ar-categories",
        label: "Categorie incassi",
        subtitle: "Categorie utilizzate per classificare gli incassi",
        icon: FolderTree,
      },
    ],
  },
  {
    key: "personalizzazioni",
    label: "Personalizzazioni",
    items: [
      {
        key: "payment-methods",
        label: "Metodi di pagamento",
        subtitle:
          "Modalità utilizzabili nelle scadenze ricorrenti (SDD, Bonifico, F24…)",
        icon: CreditCard,
      },
      {
        key: "bank-accounts",
        label: "Conti correnti",
        subtitle:
          "Conti bancari e casse dello studio — pronti per la Riconciliazione Bancaria",
        icon: Landmark,
      },
      {
        key: "tags",
        label: "Etichette",
        subtitle:
          "Tag colorati riutilizzabili per marcare movimenti (urgenti, in verifica, ecc.)",
        icon: TagIcon,
      },
      {
        key: "document-types",
        label: "Tipi documento",
        subtitle:
          "Anagrafica dei tipi di documento (Fattura, Ricevuta, F24, Nota di credito…)",
        icon: FileText,
      },
    ],
  },
  {
    key: "preferenze",
    label: "Preferenze",
    items: [
      {
        key: "preferences",
        label: "Preferenze applicative",
        subtitle:
          "Timeout sessione e valori predefiniti del Cruscotto (periodo, base)",
        icon: SlidersHorizontal,
      },
    ],
  },
  {
    key: "sistema",
    label: "Sistema",
    items: [
      {
        key: "backup",
        label: "Backup & ripristino",
        subtitle:
          "Esportazione del database e ripristino da file — la sicurezza dei dati dello studio",
        icon: DatabaseBackup,
      },
      {
        key: "branding",
        label: "Identità studio",
        subtitle:
          "Logo, denominazione e informazioni dello studio nei documenti stampati",
        icon: Building2,
      },
    ],
  },
  {
    key: "sicurezza",
    label: "Sicurezza",
    items: [
      {
        key: "security",
        label: "Cambia password",
        subtitle:
          "Aggiorna la password di accesso al gestionale (bcrypt, mai memorizzata)",
        icon: Lock,
      },
    ],
  },
];

// Flat lookup: key → { item, group }
const SECTION_INDEX = SETTINGS_GROUPS.flatMap((g) =>
  g.items.map((it) => ({ ...it, group: g })),
);
const findSection = (key) =>
  SECTION_INDEX.find((s) => s.key === key) || SECTION_INDEX[0];

/**
 * Normalizza una stringa per matching case/accent-insensitive.
 * "Metòdi di Pagaménto" → "metodi di pagamento".
 */
const normalize = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/**
 * Filtra i gruppi in base alla query di ricerca. Un gruppo compare solo
 * se ha almeno un item il cui label o subtitle matcha (fuzzy contains).
 */
function filterGroups(groups, query) {
  const q = normalize(query).trim();
  if (!q) return groups;
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (it) =>
          normalize(it.label).includes(q) ||
          normalize(it.subtitle).includes(q) ||
          normalize(g.label).includes(q),
      ),
    }))
    .filter((g) => g.items.length > 0);
}

// -----------------------------------------------------------------------
// Section renderers per Categorie e Fornitori (usano stato dal parent
// tramite props). Vivono a livello modulo per soddisfare la regola
// react/no-unstable-nested-components.
// -----------------------------------------------------------------------
function InlineSearchBar({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative min-w-[240px] flex-1 md:max-w-sm">
      <Search
        size={12}
        strokeWidth={2}
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400"
      />
      <input
        type="search"
        data-testid={testId}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 w-full rounded-md border border-stone-200 bg-stone-50 pl-7 pr-7 text-[12px] text-stone-700 outline-none transition placeholder:text-stone-400 focus:border-primary/60 focus:bg-white focus:ring-2 focus:ring-primary/10"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          data-testid={`${testId}-clear`}
          title="Pulisci ricerca"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
        >
          <X size={11} strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

function CategoriesSection({ sortedCats, openCreate, openEdit, archiveCat, restoreCat, query, setQuery }) {
  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <InlineSearchBar
          value={query}
          onChange={setQuery}
          placeholder="Cerca categoria per nome…"
          testId="search-categories"
        />
        <Button data-testid="btn-new-category" onClick={() => openCreate("category")}>
          + Nuova categoria
        </Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead className="w-24">Colore</TableHead>
              <TableHead className="w-28">Stato</TableHead>
              <TableHead className="w-56 text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedCats.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-sm text-stone-500">
                  Nessuna categoria.
                </TableCell>
              </TableRow>
            )}
            {sortedCats.map((c) => (
              <TableRow
                key={c.id}
                data-testid={`cat-row-${c.id}`}
                className={!c.is_active ? "opacity-60" : ""}
              >
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell>
                  <span
                    className="inline-block h-4 w-4 rounded-full border border-stone-200"
                    style={{ background: c.color || "#E7E5E4" }}
                  />
                </TableCell>
                <TableCell>
                  {c.is_active ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                      Attiva
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500">
                      Archiviata
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`btn-edit-cat-${c.id}`}
                      onClick={() => openEdit("category", c)}
                    >
                      <Pencil size={12} className="mr-1.5" />
                      Modifica
                    </Button>
                    {c.is_active ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`btn-archive-cat-${c.id}`}
                        onClick={() => archiveCat(c)}
                      >
                        Archivia
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`btn-restore-cat-${c.id}`}
                        onClick={() => restoreCat(c)}
                      >
                        <RefreshCw size={12} className="mr-1.5" />
                        Riattiva
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function VendorsSection({ sortedVens, openCreate, openEdit, archiveVen, restoreVen, query, setQuery }) {
  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <InlineSearchBar
          value={query}
          onChange={setQuery}
          placeholder="Cerca fornitore per nome, C.F./P.IVA, email…"
          testId="search-vendors"
        />
        <Button data-testid="btn-new-vendor" onClick={() => openCreate("vendor")}>
          + Nuovo fornitore
        </Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>P.IVA / C.F.</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="w-28">Stato</TableHead>
              <TableHead className="w-56 text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedVens.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="py-8 text-center text-sm text-stone-500">
                  Nessun fornitore.
                </TableCell>
              </TableRow>
            )}
            {sortedVens.map((v) => (
              <TableRow
                key={v.id}
                data-testid={`vendor-row-${v.id}`}
                className={!v.is_active ? "opacity-60" : ""}
              >
                <TableCell className="font-medium">{v.name}</TableCell>
                <TableCell className="text-xs text-stone-600">{v.tax_code || "—"}</TableCell>
                <TableCell className="text-xs text-stone-600">{v.email || "—"}</TableCell>
                <TableCell>
                  {v.is_active ? (
                    <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
                      Attivo
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-stone-500">
                      Archiviato
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`btn-edit-ven-${v.id}`}
                      onClick={() => openEdit("vendor", v)}
                    >
                      <Pencil size={12} className="mr-1.5" />
                      Modifica
                    </Button>
                    {v.is_active ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`btn-archive-ven-${v.id}`}
                        onClick={() => archiveVen(v)}
                      >
                        Archivia
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`btn-restore-ven-${v.id}`}
                        onClick={() => restoreVen(v)}
                      >
                        <RefreshCw size={12} className="mr-1.5" />
                        Riattiva
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

export default function Settings() {
  const [active, setActive] = useState(() => {
    // Persist via URL hash (bookmark, back button)
    if (typeof window !== "undefined" && window.location.hash) {
      const h = window.location.hash.replace(/^#/, "");
      if (SECTION_INDEX.some((s) => s.key === h)) return h;
    }
    return "categories";
  });
  const activeSection = useMemo(() => findSection(active), [active]);
  const [searchQuery, setSearchQuery] = useState("");
  const filteredGroups = useMemo(
    () => filterGroups(SETTINGS_GROUPS, searchQuery),
    [searchQuery],
  );
  const hasNoResults = searchQuery.trim() && filteredGroups.length === 0;
  const [cats, setCats] = useState([]);
  const [vens, setVens] = useState([]);
  const [dialogKind, setDialogKind] = useState(null); // 'category' | 'vendor' | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // v1.60.4 — ricerca inline case+accent-insensitive per Categorie e Fornitori
  // (stessa API di Ricerca Impostazioni, senza duplicare la logica di normalize).
  const [catsQuery, setCatsQuery] = useState("");
  const [vensQuery, setVensQuery] = useState("");

  const load = async () => {
    const [c, v] = await Promise.all([listCategories(), listVendors()]);
    setCats(c || []);
    setVens(v || []);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    // Sync hash for shareable/bookmarkable deep-link
    if (typeof window !== "undefined") {
      const desired = `#${active}`;
      if (window.location.hash !== desired) {
        window.history.replaceState(null, "", desired);
      }
    }
  }, [active]);

  useEffect(() => {
    // Ascolta cambi manuali di hash (barra URL, back/forward) e sincronizza
    // lo stato di sezione attiva senza ricaricare la pagina.
    if (typeof window === "undefined") return;
    const onHashChange = () => {
      const h = window.location.hash.replace(/^#/, "");
      if (h && SECTION_INDEX.some((s) => s.key === h) && h !== active) {
        setActive(h);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [active]);

  const openCreate = (kind) => {
    setForm({ ...EMPTY_FORM, mode: "create" });
    setError("");
    setDialogKind(kind);
  };

  const openEdit = (kind, row) => {
    setForm({
      id: row.id,
      mode: "edit",
      name: row.name || "",
      tax_code: row.tax_code || "",
      email: row.email || "",
      color: row.color || "#1F2937",
    });
    setError("");
    setDialogKind(kind);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogKind(null);
    setError("");
  };

  const submit = async () => {
    setError("");
    const trimmed = (form.name || "").trim();
    if (!trimmed) {
      setError("Il nome è obbligatorio.");
      return;
    }
    setSaving(true);
    try {
      if (dialogKind === "category") {
        const payload = { name: trimmed, color: form.color || null };
        if (form.mode === "edit") {
          await updateCategory(form.id, payload);
          toast.success("Categoria aggiornata");
        } else {
          await createCategory(payload);
          toast.success(`Categoria «${trimmed}» creata`);
        }
      } else {
        const payload = {
          name: trimmed,
          tax_code: form.tax_code?.trim() || null,
          email: form.email?.trim() || null,
        };
        if (form.mode === "edit") {
          await updateVendor(form.id, payload);
          toast.success("Fornitore aggiornato");
        } else {
          await createVendor(payload);
          toast.success(`Fornitore «${trimmed}» creato`);
        }
      }
      setDialogKind(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Errore durante il salvataggio";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const archiveCat = async (c) => {
    if (
      !window.confirm(
        `Archiviare la categoria "${c.name}"?\n\nLe spese collegate continueranno a funzionare. Potrai riattivarla in qualsiasi momento.`,
      )
    )
      return;
    try {
      await deleteCategory(c.id);
      toast.success(`Categoria "${c.name}" archiviata`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Errore"));
    }
  };
  const archiveVen = async (v) => {
    if (
      !window.confirm(
        `Archiviare il fornitore "${v.name}"?\n\nLe spese e i pagamenti collegati continueranno a funzionare. Potrai riattivarlo in qualsiasi momento.`,
      )
    )
      return;
    try {
      await deleteVendor(v.id);
      toast.success(`Fornitore "${v.name}" archiviato`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Errore"));
    }
  };

  const restoreCat = async (c) => {
    try {
      await reactivateCategory(c.id);
      toast.success(`Categoria "${c.name}" riattivata`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Errore"));
    }
  };
  const restoreVen = async (v) => {
    try {
      await reactivateVendor(v.id);
      toast.success(`Fornitore "${v.name}" riattivato`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Errore"));
    }
  };

  const sortedCats = useMemo(() => {
    const q = normalize(catsQuery).trim();
    const list = [...cats].sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "", "it", {
        sensitivity: "base",
      });
    });
    if (!q) return list;
    return list.filter((c) => normalize(c.name).includes(q));
  }, [cats, catsQuery]);

  const sortedVens = useMemo(() => {
    const q = normalize(vensQuery).trim();
    const list = [...vens].sort((a, b) => {
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "", "it", {
        sensitivity: "base",
      });
    });
    if (!q) return list;
    return list.filter(
      (v) =>
        normalize(v.name).includes(q) ||
        normalize(v.tax_code).includes(q) ||
        normalize(v.email).includes(q),
    );
  }, [vens, vensQuery]);

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Impostazioni"
        subtitle="Anagrafiche, personalizzazioni, preferenze e sicurezza del gestionale"
      />

      <div
        className="grid grid-cols-1 gap-6 md:grid-cols-[240px_minmax(0,1fr)]"
        data-testid="settings-layout"
      >
        {/* ---------------- Menu laterale ---------------- */}
        <aside
          data-testid="settings-nav"
          className="rounded-2xl border border-stone-200 bg-white p-3 md:sticky md:top-4 md:self-start"
        >
          {/* Barra di ricerca — filtra le voci del menu al volo */}
          <div className="relative mb-3">
            <Search
              size={12}
              strokeWidth={2}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400"
            />
            <input
              type="search"
              placeholder="Cerca impostazione…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="settings-search"
              className="h-8 w-full rounded-md border border-stone-200 bg-stone-50 pl-7 pr-7 text-[12px] text-stone-700 outline-none transition placeholder:text-stone-400 focus:border-primary/60 focus:bg-white focus:ring-2 focus:ring-primary/10"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                data-testid="settings-search-clear"
                title="Pulisci ricerca"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
              >
                <X size={11} strokeWidth={2} />
              </button>
            )}
          </div>

          <nav className="flex flex-col gap-4">
            {hasNoResults && (
              <div
                data-testid="settings-search-empty"
                className="rounded-md bg-stone-50 px-2.5 py-3 text-center text-[11px] leading-snug text-stone-500"
              >
                Nessuna impostazione trovata.
                <br />
                Prova con un altro termine.
              </div>
            )}
            {filteredGroups.map((group) => (
              <div key={group.key}>
                <div className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-400">
                  {group.label}
                </div>
                <div className="flex flex-col gap-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = active === item.key;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        data-testid={`nav-${item.key}`}
                        onClick={() => setActive(item.key)}
                        className={
                          "group flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-[12.5px] transition " +
                          (isActive
                            ? "bg-primary/10 font-semibold text-primary"
                            : "text-stone-700 hover:bg-stone-50 hover:text-stone-900")
                        }
                      >
                        <Icon
                          size={14}
                          strokeWidth={isActive ? 2.2 : 1.8}
                          className={isActive ? "text-primary" : "text-stone-400 group-hover:text-stone-600"}
                        />
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* ---------------- Area contenuto ---------------- */}
        <section
          data-testid="settings-content"
          className="min-w-0 rounded-2xl border border-stone-200 bg-white p-6"
        >
          {/* Section header */}
          <div className="mb-5 flex items-start gap-3 border-b border-stone-100 pb-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              {(() => {
                const Icon = activeSection.icon;
                return <Icon size={16} strokeWidth={2} />;
              })()}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-400">
                {activeSection.group.label}
              </div>
              <h2 className="font-serif text-xl font-semibold leading-tight text-stone-900">
                {activeSection.label}
              </h2>
              {activeSection.subtitle && (
                <p className="mt-0.5 text-[12px] leading-snug text-stone-500">
                  {activeSection.subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Section body — solo la sezione attiva è renderizzata */}
          {active === "categories" && (
            <div data-testid="section-categories"><CategoriesSection sortedCats={sortedCats} openCreate={openCreate} openEdit={openEdit} archiveCat={archiveCat} restoreCat={restoreCat} query={catsQuery} setQuery={setCatsQuery} /></div>
          )}
          {active === "vendors" && (
            <div data-testid="section-vendors"><VendorsSection sortedVens={sortedVens} openCreate={openCreate} openEdit={openEdit} archiveVen={archiveVen} restoreVen={restoreVen} query={vensQuery} setQuery={setVensQuery} /></div>
          )}
          {active === "ar-clients" && <ARClientsTab />}
          {active === "ar-categories" && <ARCategoriesTab />}
          {active === "payment-methods" && (
            <SimpleCrudTab
              resourceName="metodo di pagamento"
              resourceLabel="Metodi di pagamento"
              testIdPrefix="pm"
              extraDescription="Modalità utilizzabili nelle scadenze ricorrenti (Spese e Incassi). Es. SDD, Bonifico, Contanti/Cassa, Assegno, PagoPA, F24."
              fields={[
                { key: "name", label: "Nome", required: true, max: 80, placeholder: "Es. Bonifico, SDD, Contanti/Cassa" },
                { key: "description", label: "Descrizione (opzionale)", max: 255, placeholder: "Note interne" },
              ]}
              columns={[
                { header: "Nome", accessor: (r) => <span className="font-medium text-stone-800">{r.name}</span> },
                { header: "Descrizione", accessor: (r) => <span className="text-[12px] text-stone-500">{r.description || "—"}</span> },
              ]}
              loader={listPaymentMethods}
              onCreate={createPaymentMethod}
              onUpdate={updatePaymentMethod}
              onDelete={deletePaymentMethod}
              onReactivate={reactivatePaymentMethod}
            />
          )}
          {active === "bank-accounts" && (
            <SimpleCrudTab
              resourceName="conto"
              resourceLabel="Conti correnti / Casse"
              testIdPrefix="bank"
              extraDescription="Elenco dei conti bancari e delle casse dello studio. Anagrafica pronta per la futura Riconciliazione Bancaria (verrà collegata ai movimenti in Prima Nota)."
              fields={[
                { key: "name", label: "Nome", required: true, max: 120, placeholder: "Es. Intesa c/c 12345, Cassa contanti" },
                { key: "bank_name", label: "Banca", max: 120, placeholder: "Es. Intesa Sanpaolo, BPM" },
                { key: "iban", label: "IBAN (opzionale)", max: 34, placeholder: "IT00A0000000000000000000000" },
                { key: "notes", label: "Note", placeholder: "Contatto banca, filiale, ecc." },
              ]}
              columns={[
                { header: "Nome", accessor: (r) => <span className="font-medium text-stone-800">{r.name}</span> },
                { header: "Banca", accessor: (r) => <span className="text-[12px] text-stone-600">{r.bank_name || "—"}</span> },
                { header: "IBAN", accessor: (r) => <span className="font-mono text-[11px] text-stone-500">{r.iban || "—"}</span> },
              ]}
              loader={listBankAccounts}
              onCreate={createBankAccount}
              onUpdate={updateBankAccount}
              onDelete={deleteBankAccount}
              onReactivate={reactivateBankAccount}
            />
          )}
          {active === "tags" && (
            <SimpleCrudTab
              resourceName="etichetta"
              resourceLabel="Etichette colorate"
              testIdPrefix="tag"
              extraDescription="Tag colorati riutilizzabili per marcare movimenti (es. 'URGENTE', 'In verifica', 'Da controllare col commercialista')."
              fields={[
                { key: "name", label: "Nome", required: true, max: 60, placeholder: "Es. URGENTE" },
                { key: "color", label: "Colore (hex)", required: true, placeholder: "#64748B", hint: "Formato: #RRGGBB (es. #DC2626 rosso, #10B981 verde)", default: "#64748B" },
                { key: "description", label: "Descrizione", max: 255, placeholder: "Quando usare questa etichetta" },
              ]}
              columns={[
                {
                  header: "Nome",
                  accessor: (r) => (
                    <span className="inline-flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full ring-1 ring-black/5" style={{ backgroundColor: r.color }} />
                      <span className="font-medium text-stone-800">{r.name}</span>
                    </span>
                  ),
                },
                { header: "Colore", accessor: (r) => <span className="font-mono text-[11px] text-stone-500">{r.color}</span> },
                { header: "Descrizione", accessor: (r) => <span className="text-[12px] text-stone-500">{r.description || "—"}</span> },
              ]}
              loader={listTags}
              onCreate={createTag}
              onUpdate={updateTag}
              onDelete={deleteTag}
              onReactivate={reactivateTag}
            />
          )}
          {active === "document-types" && (
            <SimpleCrudTab
              resourceName="tipo documento"
              resourceLabel="Tipi documento"
              testIdPrefix="document-type"
              extraDescription="Anagrafica dei tipi di documento (Fattura, Ricevuta, F24, Nota di credito…). I tipi predefiniti sono rinominabili e ricolorabili ma non eliminabili."
              fields={[
                { key: "name", label: "Nome", required: true, max: 64, placeholder: "Es. Scontrino" },
                { key: "short_label", label: "Etichetta breve", max: 8, placeholder: "Es. SC", hint: "Mostrata nel badge in tabella (max 8 caratteri)" },
                { key: "color", label: "Colore (hex)", required: true, placeholder: "#6B7280", hint: "Formato: #RRGGBB — tinte morbide consigliate", default: "#6B7280" },
              ]}
              columns={[
                {
                  header: "Nome",
                  accessor: (r) => (
                    <span className="inline-flex items-center gap-2">
                      <span className="h-3 w-3 rounded-sm ring-1 ring-black/5" style={{ backgroundColor: r.color }} />
                      <span className="font-medium text-stone-800">{r.name}</span>
                      {r.is_system && (
                        <span className="rounded bg-stone-100 px-1.5 text-[9px] font-semibold uppercase tracking-wider text-stone-500">
                          Predefinito
                        </span>
                      )}
                    </span>
                  ),
                },
                { header: "Sigla", accessor: (r) => <span className="font-mono text-[11px] text-stone-500">{r.short_label || "—"}</span> },
                { header: "Colore", accessor: (r) => <span className="font-mono text-[11px] text-stone-500">{r.color}</span> },
              ]}
              loader={listDocumentTypes}
              onCreate={createDocumentType}
              onUpdate={updateDocumentType}
              onDelete={deleteDocumentType}
              onReactivate={reactivateDocumentType}
              disableDelete={(r) => r.is_system}
              deleteDisabledLabel="Tipo predefinito: non eliminabile"
            />
          )}
          {active === "preferences" && <PreferencesTab />}
          {active === "backup" && <BackupTab />}
          {active === "branding" && <StudioBrandingTab />}
          {active === "security" && <SecurityTab />}
        </section>
      </div>

      <Dialog open={!!dialogKind} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent data-testid="dialog-settings-form">
          <DialogHeader>
            <DialogTitle data-testid="dialog-settings-title">
              {dialogKind === "category"
                ? form.mode === "edit"
                  ? "Modifica categoria"
                  : "Nuova categoria"
                : form.mode === "edit"
                ? "Modifica fornitore"
                : "Nuovo fornitore"}
            </DialogTitle>
            <DialogDescription>
              {form.mode === "edit"
                ? "La modifica del nome aggiorna l'anagrafica. Le spese e i pagamenti collegati restano invariati e continuano a referenziare lo stesso record."
                : dialogKind === "category"
                ? "Le categorie organizzano le spese per analisi e report."
                : "I fornitori vengono associati alle spese e ai report per fornitore."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome</Label>
              <Input
                data-testid="input-name"
                value={form.name}
                autoFocus
                onChange={(e) => {
                  setForm({ ...form, name: e.target.value });
                  if (error) setError("");
                }}
              />
            </div>
            {dialogKind === "category" && (
              <div>
                <Label>Colore</Label>
                <Input
                  type="color"
                  data-testid="input-color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                />
              </div>
            )}
            {dialogKind === "vendor" && (
              <>
                <div>
                  <Label>P.IVA / Codice fiscale</Label>
                  <Input
                    data-testid="input-taxcode"
                    value={form.tax_code}
                    onChange={(e) =>
                      setForm({ ...form, tax_code: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    data-testid="input-email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                  />
                </div>
              </>
            )}
            {error && (
              <div
                data-testid="form-error"
                className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
              >
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeDialog}
              disabled={saving}
            >
              Annulla
            </Button>
            <Button
              data-testid="btn-settings-submit"
              onClick={submit}
              disabled={saving}
            >
              {saving
                ? "Salvataggio…"
                : form.mode === "edit"
                ? "Salva modifiche"
                : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
